
import { Block } from "../shapes/Block";
import { Point } from "../Point";
import { s } from "../../shared/globalState/settings";
import { createElbowBlockLines } from "../../shared/utils/lineFactory";
import { Line } from "../../models/shapes/Line";
import { RouteCrucialPoints, Shape, FlowDirection } from "../../shared/types";
import { ObjectTypes } from "../../shared/enums";
import { Vector } from "../Vector";
import { getPointByVectorAndLength } from "../../shared/common";
import { densityAir, ElbowRect, ElbowRound, hydraulicDiameter, kinViscAir, lambdaKoef, reinolds, velocityRect, velocityRound } from "../../libs/hydraulic-resistance";
import { BlockData } from "../../models/BlockData";
import { ifPointOnLine, isin } from "../../shared/common";
import { mat3 } from "gl-matrix";
import { transformPointByMatrix3 } from "../../shared/common";

interface RefSegment {
  position: `base` | `side`,
  segment: Line,
  whichEnd: `start` | `end`,
}
export class ElbowBlock extends Block {

  private _center!: Point;
  private _base!: Point;
  private _side!: Point;

  private _angle!: number;
  private _widthMil: number | null = null;
  private _heightMil: number | null = null;
  private _diameterMil: number | null = null;
  private _flowCmh: number = 0;

  private _vecBase!: Vector;
  private _vecCurrent!: Vector;

  _segmentBase!: Line;
  _segmentCurrent!: Line;

  elbowType: string = '';
  type: string = ObjectTypes.elbowBlock;

  rd: number = 0;
  size: number[] | number = 0;
  roughness: number = 0.1;
  temperature: number = 20;
  kms: number = 0;
  localDp: number = 0;
  color: number[] = s.elbowColor;

  // ***
  sideLength: number = 12;
  flangeLength: number = 10;

  declare data: BlockData;


  refSegmentBase: RefSegment | null = null;
  refSegmentSide: RefSegment | null = null;

  get vecBase(): Vector {
    return this._vecBase;
  }
  get vecCurrent(): Vector {
    return this._vecCurrent;
  }

  get center() {
    return this._center;
  }

  set center(center: Point) {
    this._center = center;
    this.updateGeometry();
  }

  get base() {
    return this._base;
  }

  set base(base: Point) {
    this._base = base;
    this.updateGeometry();

  }

  get side() {
    return this._side;
  }

  set side(side: Point) {
    this._side = side;
    this.updateGeometry();

  }

  get angle() {
    return this._angle;
  }

  set angle(angle: number) {
    this._angle = angle;
    this.updateGeometry();
  }

  get widthMil() {
    return this._widthMil;
  }
  set widthMil(v) {
    this._resetDiameter();
    this._widthMil = v;
    this.updateResistance();
  }
  get heightMil() {
    return this._heightMil;
  }
  set heightMil(v) {
    this._resetDiameter();
    this._heightMil = v;
    this.updateResistance();
  }
  get diameterMil() {
    this._resetWidthAndHeight();
    return this._diameterMil;
  }
  set diameterMil(v) {
    this._resetWidthAndHeight();
    this._diameterMil = v;
    this.updateResistance();
  }
  get flowCmh() {
    return this._flowCmh;
  }
  set flowCmh(v) {
    this._flowCmh = v;
    this.updateResistance();
  }


  constructor(center: Point,
    sideLength: number,
    angleDeg: number,
    segmentBase: Line,
    segmentCurrent: Line,
    rd: number,
    size: number[] | number,
    flowCmh: number
  ) {

    var shapes = generateShapes(s.elbowType);
    super(shapes, s.elbowColor, s.blockThickness);

    this.updateBaseAndSide(center, segmentBase, segmentCurrent);

    this.angle = angleDeg;

    this.center = center;
    this.sideLength = sideLength;


    this.elbowType = 'simpleElbow';
    this.temperature = 20;
    this.roughness = 0.1;

    this.rd = rd;
    if (Array.isArray(size)) {
      this._widthMil = size[0];
      this._heightMil = size[1];
    }
    else {
      this._diameterMil = size;
    }

    this.size = Array.isArray(size) ? Array.from(size) : size;
    this._flowCmh = flowCmh;

    this.data.code = 'Отвод';
    this.data.angle = angleDeg;

    this.updateGeometry();
    this.updateResistance();

  }

  /**
   * Функция вызывается один раз для того, чтобы добавить в массив refSegments присоединённые
   * к отводу отрезки. Функция должна вызываться уже после функции alignRefSegments, 
   * иначе функция whichEnd будет возвращать null
   * @param segmentBase отрезок, с которого начинается черчение
   * @param segmentCurrent текущий отрезок
   */
  initLinkRefSegments(segmentBase: Line, segmentCurrent: Line) {

    var refSegmentBase: RefSegment = {
      position: 'base',
      segment: segmentBase,
      whichEnd: segmentBase.getWhichEnd(this.base) // каким концом отрезок присоединён к точке this.base
    };

    var refSegmentSide: RefSegment = {
      position: 'side',
      segment: segmentCurrent,
      whichEnd: segmentCurrent.getWhichEnd(this.side) // каким концом отрезок присоединён к точке this.side
    }

    this.refSegmentBase = refSegmentBase;
    this.refSegmentSide = refSegmentSide;
  }

  updateLinkRefSegments() {

    if (!this.refSegmentBase?.whichEnd || !this.refSegmentSide?.whichEnd) return;

    // Update line endpoints while preserving Point references
    if (this.refSegmentBase.whichEnd === 'start') {
      this.refSegmentBase.segment.updateEndpoint('start', this.base);
    }
    else if (this.refSegmentBase.whichEnd === 'end') {
      this.refSegmentBase.segment.updateEndpoint('end', this.base);
    }
    if (this.refSegmentSide.whichEnd === 'start') {
      this.refSegmentSide.segment.updateEndpoint('start', this.side);
    }
    else if (this.refSegmentSide.whichEnd === 'end') {
      this.refSegmentSide.segment.updateEndpoint('end', this.side);
    }
  }

  /**
   * Функция "присоединяет" концы отрезков к концам отвода
   * @param segmentBase исходный отрезок, то есть тот, с которого или начинается черчение или на котором заканчивается
   * @param segmentCurrent текущий отрезок
   */
  connectRefSegments(segmentBase: Line, segmentCurrent: Line) {

    // если черчение начинается с segmentBase
    if (isin(segmentBase.start, segmentCurrent.start)) {
      segmentBase.start = this.base;
      segmentCurrent.start = this.side;
    }
    else if (isin(segmentBase.end, segmentCurrent.start)) {
      segmentBase.end = this.base;
      segmentCurrent.start = this.side;
    }

    // если черчение заканчивается на segmentBase
    else if (isin(segmentBase.start, segmentCurrent.end)) {
      segmentBase.start = this.base;
      segmentCurrent.end = this.side;
    }
    else if (isin(segmentBase.end, segmentCurrent.end)) {
      segmentBase.end = this.base;
      segmentCurrent.end = this.side;
    }

    this.initLinkRefSegments(segmentBase, segmentCurrent);
  }

  disconnectRefSegments(): void {
    this.refSegmentBase = null;
    this.refSegmentSide = null;
  }

  updateAngle(segment1: Line, segment2: Line) {
    this.angle = segment1.getAngleBetween(segment2);
  }

  updateBaseAndSide(center: Point, segmentBase: Line, segmentSide: Line) {
    // Validate inputs
    if (!center || !segmentBase || !segmentSide) {
      console.error('ElbowBlock.updateBaseAndSide: Invalid parameters provided', {
        center: !!center,
        segmentBase: !!segmentBase,
        segmentSide: !!segmentSide
      });
      return;
    }

    this._center = center;
    this._segmentBase = segmentBase;
    this._segmentCurrent = segmentSide;

    // определить два вектора по соответствующим отрезкам
    try {
      this._vecBase = this._segmentBase.getVector(center);
      this._vecCurrent = this._segmentCurrent.getVector(center);
    } catch (error) {
      console.error('ElbowBlock.updateBaseAndSide: Error getting vectors', error);
      // Set default vectors to prevent further errors
      this._vecBase = new Vector(center, center);
      this._vecCurrent = new Vector(center, center);
    }

    // найти точки base и side на этих векторах
    try {
      var basePoint = getPointByVectorAndLength(this.vecBase, this.sideLength);
      var sidePoint = getPointByVectorAndLength(this.vecCurrent, this.sideLength);

      this.base = new Point(center.x + basePoint.x, center.y + basePoint.y);
      this.side = new Point(center.x + sidePoint.x, center.y + sidePoint.y);
    } catch (error) {
      console.error('ElbowBlock.updateBaseAndSide: Error computing base/side points', error);
      // Set fallback positions
      this.base = center.clone();
      this.side = center.clone();
    }
  }

  updateGeometry() {
    this.data.angle = this.angle;

    if (this.elbowType === ObjectTypes.simpleElbow) {

      let baseSide = this.shapes[0] as Line;
      let secondSide = this.shapes[1] as Line;
      let baseFlange = this.shapes[2] as Line;
      let secondFlange = this.shapes[3] as Line;

      baseSide.start = this.center;

      baseSide.end = this.base;

      var baseFlangeVec = this.vecBase.getPerpendicularVectorAtPoint(baseSide.end!);
      var baseFlangeOppositeVec = this.vecBase.getOppositeToPerpendicularVectorAtPoint(baseSide.end!);

      var baseFlangeStartPoint = getPointByVectorAndLength(baseFlangeOppositeVec, this.flangeLength / 2);
      baseFlange.start = new Point(
        baseFlangeVec.start.x + baseFlangeStartPoint.x,
        baseFlangeVec.start.y + baseFlangeStartPoint.y
      );

      var baseFlangeEndPoint = getPointByVectorAndLength(baseFlangeVec, this.flangeLength / 2);
      baseFlange.end = new Point(
        baseFlangeOppositeVec.start.x + baseFlangeEndPoint.x,
        baseFlangeOppositeVec.start.y + baseFlangeEndPoint.y
      );

      secondSide.start = this.center;


      secondSide.end = this.side;

      var secondFlangeVec = this.vecCurrent.getPerpendicularVectorAtPoint(secondSide.end!);
      var secondFlangeOppositeVec = this.vecCurrent.getOppositeToPerpendicularVectorAtPoint(secondSide.end!);

      var secondFlangeStartPoint = getPointByVectorAndLength(secondFlangeOppositeVec, this.flangeLength / 2);
      secondFlange.start = new Point(
        secondFlangeVec.start.x + secondFlangeStartPoint.x,
        secondFlangeVec.start.y + secondFlangeStartPoint.y
      );

      var secondFlangeEndPoint = getPointByVectorAndLength(secondFlangeVec, this.flangeLength / 2);
      secondFlange.end = new Point(
        secondFlangeOppositeVec.start.x + secondFlangeEndPoint.x,
        secondFlangeOppositeVec.start.y + secondFlangeEndPoint.y
      );

    }
  }


  private _resetDiameter() {
    this._diameterMil = null;
  }
  private _resetWidthAndHeight() {
    this._widthMil = null;
    this._heightMil = null;
  }

  getKms(): number | null {
    if (this._widthMil !== null && this._widthMil !== undefined && this._heightMil !== null && this._heightMil !== undefined) {
      var diamMet = hydraulicDiameter(this._widthMil / 1000, this._heightMil / 1000);
      var v = velocityRect(this._widthMil, this._heightMil, this._flowCmh);
      var nu = kinViscAir(this.temperature);
      var re = reinolds(v, diamMet, nu);
      var lambda = lambdaKoef(re, diamMet * 1000, this.roughness);

      var kms = ElbowRect.resistance(this.roughness, this.angle, this.rd, lambda, re, this._widthMil, this._heightMil);
      return kms;
    }
    else if (this._diameterMil !== null && this._diameterMil !== undefined) {
      var diamMet = this._diameterMil / 1000;
      var v = velocityRound(this._diameterMil, this._flowCmh);

      var nu = kinViscAir(this.temperature);
      var re = reinolds(v, diamMet, nu);
      var lambda = lambdaKoef(re, diamMet * 1000, this.roughness);

      var kms = ElbowRound.resistance(this.roughness, this.angle, this.rd, lambda, re, 0, 0, this._diameterMil);
      return kms;
    }

    return null;
  }

  getLocalDp(): number {
    var density = densityAir(this.temperature);
    let v = 0;
    if (this._diameterMil !== null && this._diameterMil !== undefined) {
      v = velocityRound(this._diameterMil, this._flowCmh);
    }
    else if (this._widthMil !== null && this._widthMil !== undefined && this._heightMil !== null && this._heightMil !== undefined) {
      v = velocityRect(this._widthMil, this._heightMil, this._flowCmh);
    }

    var localDp = this.kms * (density * v ** 2) / 2;
    return localDp;
  }

  updateResistance(): void {
    const kmsValue = this.getKms();
    this.kms = kmsValue ?? 0;
    this.localDp = this.getLocalDp();
    this.net.kms = this.kms;
    this.net.dp = this.localDp;
  }

  override zoom(zl: number): void {
    this.shapes.forEach(s => {
      s.zoom(zl);
    });

    this.cpoints.forEach(p => {
      p.zoom(zl);
    });

    var zoom_mat = mat3.fromScaling(mat3.create(), new Float32Array([zl, zl, 1]));
    // Preserve point identities: mutate coordinates instead of replacing instances
    const newCenter = transformPointByMatrix3(zoom_mat, this._center);
    this._center.x = newCenter.x;
    this._center.y = newCenter.y;

    const newBase = transformPointByMatrix3(zoom_mat, this._base);
    this._base.x = newBase.x;
    this._base.y = newBase.y;

    const newSide = transformPointByMatrix3(zoom_mat, this._side);
    this._side.x = newSide.x;
    this._side.y = newSide.y;
    
    this.updateSavedVertices();
  }

  override pan(tx: number, ty: number): void {
    this.shapes.forEach(s => {
      s.pan(tx, ty);
    });

    this.cpoints.forEach(p => {
      p.pan(tx, ty);
    });

    var pan_mat = mat3.fromTranslation(mat3.create(), new Float32Array([tx, ty, 0]));
    mat3.transpose(pan_mat, pan_mat);
    // Preserve point identities: mutate coordinates instead of replacing instances
    const newCenter = transformPointByMatrix3(pan_mat, this._center);
    this._center.x = newCenter.x;
    this._center.y = newCenter.y;

    const newBase = transformPointByMatrix3(pan_mat, this._base);
    this._base.x = newBase.x;
    this._base.y = newBase.y;

    const newSide = transformPointByMatrix3(pan_mat, this._side);
    this._side.x = newSide.x;
    this._side.y = newSide.y;


    this.updateSavedVertices();

  }

  assignConnectionPoints() {
    let connectionPoint1 = this.base;
    let connectionPoint2 = this.side;

    super.assignConnectionPoint(connectionPoint1);
    super.assignConnectionPoint(connectionPoint2);
  }

  /**
   * Функция возвращает объект с координатами характерных точек, эти точки используются для определения маршрутов
   * @returns {
   *   start: Point,
   *   end: Point
   * }
   */
  override getCrucialPoints(): RouteCrucialPoints {
    return {
      start: this.base,
      end: this.side,
      flowDirections: {
        start: FlowDirection.UNKNOWN,
        end: FlowDirection.UNKNOWN
      },
      isTerminal: {
        start: false,
        end: false
      },
      pressure: {
        start: 0,
        end: 0
      }
    }
  }



}

function generateShapes(elbowType: string) {
  return createElbowBlockLines();
}